// Pragmatic port of TimelineView; see ../CardsViewPragmatic/README.md for the
// rationale and known HTML5-DnD limitations (same trade-offs apply here).
import {
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState
} from 'react';

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import { installGlobalDragListeners } from './installGlobalDragListeners';
import { useMediaQuery } from 'usehooks-ts';
import { format, isValid } from 'date-fns';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

// components
import { LoadingOverlay, showSnackbar } from 'components/shared';
import TaskList from 'components/shared/TaskList/TaskList';
import type { SortedToPlanListBaseProps } from 'components/shared/TaskList/types';
import ToPlanDropzonePragmatic from '../CardsViewPragmatic/ToPlanDropzonePragmatic/ToPlanDropzonePragmatic';
import DragPortalPragmatic from '../CardsViewPragmatic/DragPortalPragmatic/DragPortalPragmatic';
import SortedToPlanChipPragmatic from './SortedToPlanChipPragmatic/SortedToPlanChipPragmatic';
import { PopOverConfirmationContext } from '../PopOverConfirmationContext';
import TimelineRoutePragmatic from './TimelineRoutePragmatic/TimelineRoutePragmatic';
import Summary from '../TimelineView/TimelineRoute/Summary';
import { TimelineBar } from '../TimelineView/TimelineBar';

// hooks
import { useElementSize, useNavParams, useSelector } from 'hooks';
import { useRecoilState, useRecoilValue } from 'recoil';
import useConnectScheduleAction from '../hooks/useConnectScheduleAction';
import useTimelineInterval from '../hooks/useTimelineInterval';
import usePlanningEntity from '../hooks/usePlanningEntity';
import type { UsePlanningReturn } from '../hooks/usePlanning';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';

// styles
import { TIMELINE_SUMMARY_WIDTH } from '../TimelineView/TimelineRoute/Summary/Summary';
import {
  getScrollContainerSx,
  getScrollWrapperSx,
  stickySummarySx
} from './TimelineViewPragmatic.styles';

// utils
import type {
  DayPart,
  PlanAppointmentsRequest,
  ScheduleResponse
} from 'generated';
import type { Event, PlanningEntity, PlanningEntityMap } from '../types';
import type { EditAppointmentType } from 'RestAPIs/activities/types';
import {
  type APNotification,
  appendCorrelationToken,
  findItem,
  getConflictingEvents,
  handlePlanAppointments as handlePlanAppointmentsUtil,
  notificationsToHashTable,
  planningEntitiesToEvents,
  unassignAppointment as unassignAppointmentUtil
} from '../utils';
import {
  getItemWidthInPixels,
  getSecondsPerPixel,
  getTimeBarItemWidth
} from '../TimelineView/utils';
import type { ConnectAppointmentGroupingPayload } from 'RestAPIs/activities';
import { mutations } from 'RestAPIs/rapini';
import {
  dialogManagerActiveAtom,
  dragActionActiveAtom,
  isToPlanDrawerOpenAtom,
  planResponsePlanningEntityIdentifiersAtom,
  selectedDayPartsAtom,
  selectedGroupingScheduleAtom,
  shouldShowPopoverDialogAtom
} from '../atoms';
import { frozenDropAtom } from '../TimelineView/atoms';
import { setUserPreference } from '../../../../redux/actionCreators';
import { View } from '../enums';
import { ZoomLevel } from '../TimelineView/TimelineBar/TimelineBar.enums';
import { ZoomLevel as DetailZoomLevel } from '../components/Header/ZoomLevelMenu.enums';
import { DialogType } from '../components/DialogManager/types';
import { useTimelineDragAutoScroll } from './TimelineViewPragmatic.hooks';
import {
  buildDropPayload,
  computeDropStart,
  getChipLeftInRoute,
  getDropDate,
  resolveDraggedEvent,
  TIMELINE_DRAGGABLE_SCROLL_CONTAINER_CLASS
} from './TimelineViewPragmatic.utils';
import { buildChipPreviewData } from './SortedToPlanChipPragmatic/DraggableTimelineCardPragmatic.utils';
import FrozenDropChipPragmatic from './FrozenDropChipPragmatic';

// Install once at module load so listeners exist before any drag can start.
installGlobalDragListeners();

const SortedToPlanChipWrapper = ({
  zoomLevel,
  timeBarItemWidth
}: {
  zoomLevel: ZoomLevel;
  timeBarItemWidth: number;
}) => {
  'use memo';
  return (props: SortedToPlanListBaseProps) => (
    <SortedToPlanChipPragmatic
      {...props}
      zoomLevel={zoomLevel}
      timeBarItemWidth={timeBarItemWidth}
    />
  );
};

export default function TimelineViewPragmatic({
  startDate,
  endDate,
  currentPlanningEntityIdentifiers,
  planningEntities,
  setPlanningEntities,
  planAppointments,
  connectAppointmentGrouping,
  connectSchedule,
  revertAction,
  isLoading,
  showEvents
}: UsePlanningReturn) {
  'use memo';
  const dispatch = useDispatch();
  const {
    defaultDayTimelineZoomLevel,
    defaultWeekTimelineZoomLevel,
    defaultZoomLevel
  } = useSelector((state) => state.preferences);
  // Chip detail zoom (COMPACT/STANDARD/DETAILED) for the frozen-drop preview.
  const detailZoomLevel = defaultZoomLevel ?? DetailZoomLevel.STANDARD;

  const [, setDialogState] = useRecoilState(dialogManagerActiveAtom);
  const [taskListIsOpen, setTaskListIsOpen] = useState<boolean>(true);

  const [shouldShowPopoverDialog, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );

  const [isToPlanDrawerOpen] = useRecoilState(isToPlanDrawerOpenAtom);
  const showToPlanDrawer = isToPlanDrawerOpen && showEvents;
  const { t } = useTranslation('common', { keyPrefix: 'activityPlanning' });

  const { params } = useNavParams();
  const view = params?.view as View;

  const [planningEntityTypePreference] = usePlanningEntity();
  // TimelinePragmatic shares the entity-type preference with Timeline.
  const planningEntityType =
    planningEntityTypePreference[
      view === View.TimelinePragmatic ? View.Timeline : view
    ];

  const [
    isLoadingUnassignAppointment,
    setIsLoadingUnassignAppointmentFromEmployee
  ] = useState<boolean>(false);

  const { mutateAsync: unassignAppointmentAsync } =
    mutations.useRemoveAssignmentFromAppointments();

  const [payload, setPayload] = useState<
    PlanAppointmentsRequest | ConnectAppointmentGroupingPayload
  >();

  const [activeEvent, setActiveEvent] = useState<Event | null>(null);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );

  const [pendingDropPosition, setPendingDropPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const selectedDayParts = useRecoilValue(selectedDayPartsAtom);
  const dateNavigationPeriod = useDateNavigationStore(({ period }) => period);

  const [, setDragActionActive] = useRecoilState<boolean>(dragActionActiveAtom);

  const [, setFrozenDrop] = useRecoilState(frozenDropAtom);

  // The freeze is held until the refetch settles (loading goes false after
  // having been true) so the card un-fades and the cursor overlay disappears at
  // the same instant it snaps to its final position.
  const frozenActiveRef = useRef(false);
  const sawFreezeLoadingRef = useRef(false);

  // Release the frozen drag frame (cursor overlay + dimmed view).
  const releaseFrozenDrop = () => {
    frozenActiveRef.current = false;
    sawFreezeLoadingRef.current = false;
    setFrozenDrop(null);
    dragContainerRef.current?.removeAttribute('data-drag-active');
  };
  // Stable ref so effects can call the latest release without taking it as a
  // dependency (matches the handler-ref pattern used for the drag monitor).
  const releaseFrozenDropRef = useRef(releaseFrozenDrop);
  releaseFrozenDropRef.current = releaseFrozenDrop;

  // Release on unmount so the global frozenDropAtom can't flash a stale preview on remount.
  useEffect(() => {
    return () => {
      releaseFrozenDropRef.current();
    };
  }, []);

  const selectedGroupingSchedule = useRecoilValue(selectedGroupingScheduleAtom);

  // Width unused — native drag previews replace the dnd-kit overlay sizing.
  const [containerRef] = useElementSize([
    !!selectedGroupingSchedule,
    isLoading
  ]);

  const isTablet = useMediaQuery('(max-width: 1200px)');
  const [isDraggingTimeline, setIsDraggingTimeline] = useState<boolean>(false);

  const [, setCurrentVisibleDay] = useState<Date | null>(null);
  const [dayZoomLevel, setDayZoomLevel] = useState<ZoomLevel>(
    defaultDayTimelineZoomLevel ?? ZoomLevel.M
  );
  const [weekZoomLevel, setWeekZoomLevel] = useState<ZoomLevel>(
    defaultWeekTimelineZoomLevel ?? ZoomLevel.S
  );

  const [activeEntityContainingSelection, setActiveEntityContainingSelection] =
    useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Imperative data-drag-active toggle read via CSS in chip components,
  // avoiding ~5 000 Recoil subscriptions.
  const dragContainerRef = useRef<HTMLDivElement>(null);

  const zoomLevel =
    dateNavigationPeriod === 'week' || dateNavigationPeriod === 'workweek'
      ? weekZoomLevel
      : dayZoomLevel;

  const setZoomLevel = (level: ZoomLevel): void => {
    if (
      dateNavigationPeriod === 'week' ||
      dateNavigationPeriod === 'workweek'
    ) {
      setWeekZoomLevel(level);
    } else {
      setDayZoomLevel(level);
    }
  };

  const onMutationError = () => {
    setPayload(undefined);
    releaseFrozenDrop();
    revertAction();
  };

  const filterPlanningEntitiesForEmployeePerspective = (
    planningEntity: PlanningEntity
  ) => {
    if (planningEntityType === 'EMPLOYEE')
      return planningEntity.type !== 'EMPLOYEE';
    return true;
  };

  const filterTimelineByDayPart = (planningEntities: PlanningEntityMap) =>
    Object.values(planningEntities)
      .filter(filterPlanningEntitiesForEmployeePerspective)
      .filter(
        (props) =>
          props.type !== 'ORGANIZATIONAL_UNIT_TO_PLAN' &&
          (selectedDayParts.length === 0 ||
            selectedDayParts.includes(props.schedules?.[0]?.dayPart as DayPart))
      );

  // Single pass that builds both schedules and events for useTimelineInterval —
  // halves the per-render work vs. the previous getFilteredEvents/Schedules pair.
  const filteredTimelineEvents = (() => {
    if (!planningEntities) return [];
    const filteredPlanningEntities = filterTimelineByDayPart(planningEntities);
    if (filteredPlanningEntities.length === 0) return [];

    const schedules = filteredPlanningEntities.flatMap(
      (entity) => (entity.schedules ?? []) as ScheduleResponse[]
    );
    const events = schedules.flatMap(
      (schedule) => (schedule.events ?? []) as Event[]
    );
    return [
      ...events.map((event) => ({ ...event, droppable: true })),
      ...schedules
    ];
  })();

  const interval = useTimelineInterval(filteredTimelineEvents, zoomLevel);
  const timeBarItemWidth = getTimeBarItemWidth(
    interval,
    scrollContainerRef.current?.clientWidth || 0
  );

  const sortedFilteredEntities = (() => {
    if (!planningEntities) return [];
    return filterTimelineByDayPart(planningEntities).sort(
      ({ name: a }, { name: b }) => (a ?? '').localeCompare(b ?? '') ?? 0
    );
  })();

  const timelineZoomWidth =
    timeBarItemWidth * (interval.timeWindow?.length || 0);

  const handlePlanAppointments = (payload: PlanAppointmentsRequest | null) => {
    handlePlanAppointmentsUtil({
      payload,
      planAppointments,
      setPayload,
      setShouldShowPopoverDialog,
      setDialogState,
      revertAction,
      onMutationError,
      planningEntitiesToEvents,
      getConflictingEvents,
      notificationsToHashTable,
      planningEntities,
      appendCorrelationToken,
      setPlanResponsePlanningEntityIdentifiers,
      setPlanningEntities,
      viewport: {
        startDate,
        endDate,
        planningEntityIdentifiers: currentPlanningEntityIdentifiers
      },
      onCancelWarningDialog: () => {
        releaseFrozenDrop();
        setActiveEvent(null);
        setPendingDropPosition(null);
      },
      onSuccess: () => {
        setSelectedIds([]);
        setActiveEntityContainingSelection(null);
        setActiveEvent(null);
        setPendingDropPosition(null);
      },
      onComplete: () => {
        setSelectedIds([]);
        // Frozen frame is released by the loading-settled effect, so it clears
        // exactly when the card snaps to its final position.
      }
    });
  };

  const sendConnectAppointmentGrouping = (
    appointmentGroupingEntityId: string,
    editAppointmentType?: EditAppointmentType,
    scheduleStart?: string
  ) => {
    if (planningEntities) {
      const payload: ConnectAppointmentGroupingPayload = {
        appointmentGroupingId: appointmentGroupingEntityId,
        appointmentGroupingStart: startDate,
        editAppointmentType: editAppointmentType || 'ONLY_THIS',
        suppressWarnings: false
      };
      setPayload(payload);
      const planningEntity = planningEntities[appointmentGroupingEntityId];
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);

      if (
        planningEntity.schedules
          .map((s) => s.events)
          .flat()
          .some((event) => event.isRecurring && !event.isAbsent) &&
        new Date(scheduleStart ?? startDate) >= today
      ) {
        setShouldShowPopoverDialog({
          id: appointmentGroupingEntityId,
          scheduleStart,
          isRouteConnect: true
        });
      } else {
        connectAppointmentGrouping(payload, {
          onSuccess: (response: any) => {
            if (!response.data.success) {
              const appointmentOccurrences =
                planningEntities[payload.appointmentGroupingId ?? ''].schedules
                  .map((s) => s.events)
                  .flat() ?? [];
              const notifications = response.data
                .notifications as APNotification[];
              const conflictingEvents: Event[] = getConflictingEvents(
                appointmentOccurrences || [],
                notifications
              );

              setDialogState({
                type: DialogType.WARNING,
                open: true,
                data: notificationsToHashTable(
                  conflictingEvents,
                  notifications
                ),
                onSubmit: () => {
                  connectAppointmentGrouping(
                    {
                      ...payload,
                      suppressWarnings: true
                    } as ConnectAppointmentGroupingPayload,
                    {
                      onError: onMutationError
                    }
                  );
                },
                onCancel: () => {
                  revertAction();
                  releaseFrozenDrop();
                }
              });

              setActiveEvent(null);
              setPendingDropPosition(null);
            }
          },
          onError: onMutationError
        });
      }
    }
  };

  const sendConnectSchedule = useConnectScheduleAction({
    connectSchedule,
    defaultScheduleStart: startDate,
    onError: onMutationError,
    planningEntities
  });

  const unassignAppointment = (editAppointmentType: EditAppointmentType) => {
    unassignAppointmentUtil({
      editAppointmentType,
      activeEvent,
      selectedIds,
      planningEntities,
      shouldShowPopoverDialog,
      appendCorrelationToken,
      unassignAppointmentAsync,
      setShouldShowPopoverDialog,
      setPlanResponsePlanningEntityIdentifiers,
      setActiveEntityContainingSelection,
      setIsLoading: setIsLoadingUnassignAppointmentFromEmployee,
      onFinally: () => {
        setIsLoadingUnassignAppointmentFromEmployee(false);
        // Frozen frame is released by the loading-settled effect once the
        // unassign refetch finishes, so it clears as the card lands in To plan.
        setActiveEvent(null);
        setActiveEntityContainingSelection(null);
        setPendingDropPosition(null);
        setSelectedIds([]);
      },
      onGetEvents: (planningEntity, isToPlan, selectedIds) => {
        return (
          (isToPlan
            ? planningEntity?.planningTasks
                ?.map((task: any) => task.data)
                .filter(
                  (event: Event) =>
                    event?.id !== undefined && selectedIds.includes(event.id)
                )
            : planningEntity?.schedules
                .flatMap((s: any) => s.events)
                .filter(({ id }: { id: string }) => selectedIds.includes(id))
          )?.filter(
            (event: Event | undefined): event is Event => event !== undefined
          ) || []
        );
      }
    });
  };

  const onHandleAppointmentConfirmation = (
    editAppointmentType: EditAppointmentType
  ) => {
    if (shouldShowPopoverDialog.isUnassignment) {
      return unassignAppointment(editAppointmentType);
    } else if (shouldShowPopoverDialog.isRouteConnect) {
      const payload: ConnectAppointmentGroupingPayload = {
        appointmentGroupingId: shouldShowPopoverDialog.id,
        appointmentGroupingStart:
          shouldShowPopoverDialog.scheduleStart || startDate,
        editAppointmentType: editAppointmentType || 'ONLY_THIS',
        suppressWarnings: false
      };

      return connectAppointmentGrouping(payload, {
        onSuccess: (response: any) => {
          if (!response.data.success) {
            const appointmentOccurrences =
              planningEntities?.[payload.appointmentGroupingId ?? ''].schedules
                .map((s) => s.events)
                .flat() ?? [];
            const notifications = response.data
              .notifications as APNotification[];
            const conflictingEvents: Event[] = getConflictingEvents(
              appointmentOccurrences || [],
              notifications
            );

            setDialogState({
              type: DialogType.WARNING,
              open: true,
              data: notificationsToHashTable(conflictingEvents, notifications),
              onSubmit: () => {
                connectAppointmentGrouping(
                  {
                    ...payload,
                    suppressWarnings: true
                  } as ConnectAppointmentGroupingPayload,
                  {
                    onError: onMutationError
                  }
                );
              },
              onCancel: () => {
                revertAction();
                releaseFrozenDrop();
              }
            });
          } else {
            setActiveEvent(null);
            setPendingDropPosition(null);
          }

          setShouldShowPopoverDialog({ id: '' });
        },
        onError: onMutationError
      });
    }

    handlePlanAppointments({
      ...(payload as PlanAppointmentsRequest),
      editAppointmentType
    });
  };

  const onReset = () => {
    revertAction();
    releaseFrozenDrop();
    setActiveEvent(null);
    setPendingDropPosition(null);
    setSelectedIds([]);
    setShouldShowPopoverDialog({
      id: '',
      isUnassignment: false,
      isRouteUnassignment: false,
      isRouteConnect: false
    });
  };

  function scrollTimeline(direction: 'forward' | 'backward') {
    const { clientWidth, scrollLeft } = (scrollContainerRef.current || {
      clientWidth: 0,
      scrollLeft: 0,
      scrollWidth: 0
    }) as HTMLDivElement;
    const x = scrollLeft || 0;
    const jumpWidth = (clientWidth || 0) * 0.5;

    scrollContainerRef.current?.scrollTo({
      left: direction === 'forward' ? x + jumpWidth : x - jumpWidth,
      behavior: 'smooth'
    });
  }

  // Pan via left-mouse drag of empty timeline area — pure mouse events, no DnD.
  function dragTimeline(e: ReactMouseEvent) {
    const startPos = scrollContainerRef.current?.scrollLeft || 0;
    const movement = e?.movementX || 0;
    const speed = 1.5;

    scrollContainerRef.current?.scrollTo({ left: startPos - movement * speed });
  }

  function scrollToDay(selectedDate: Date, routeId?: string) {
    if (!scrollContainerRef.current) return;

    const secondsPerPixel = getSecondsPerPixel(timeBarItemWidth, zoomLevel);
    const timeDifferenceInSeconds =
      (selectedDate.getTime() - interval.start.getTime()) / 1000;

    const horizontalScrollPosition = timeDifferenceInSeconds / secondsPerPixel;

    let verticalScrollPosition = 0;

    if (routeId) {
      const routeElement = document.querySelector(
        `.__${routeId}__`
      ) as HTMLElement | null;
      if (routeElement) {
        verticalScrollPosition =
          routeElement.offsetTop - scrollContainerRef.current.offsetTop - 50;

        routeElement.classList.add('highlighted');
        setTimeout(() => {
          routeElement.classList.remove('highlighted');
        }, 4000);
      }
    }

    scrollContainerRef.current.scrollTo({
      left: horizontalScrollPosition,
      top: verticalScrollPosition,
      behavior: 'smooth'
    });
  }

  useEffect(() => {
    setActiveEvent(null);
    setPendingDropPosition(null);
    setSelectedIds([]);
    releaseFrozenDropRef.current();
    setPayload(undefined);
  }, [planningEntityType]);

  // Release the frozen frame the moment the data settles: once loading has been
  // true (request in flight) and turns false (refetch done), the card snaps to
  // its final spot at full opacity and the cursor overlay clears simultaneously.
  useEffect(() => {
    if (!frozenActiveRef.current) return;
    if (isLoading || isLoadingUnassignAppointment) {
      sawFreezeLoadingRef.current = true;
    } else if (sawFreezeLoadingRef.current) {
      releaseFrozenDropRef.current();
    }
  }, [isLoading, isLoadingUnassignAppointment]);

  // Stable handler refs avoid stale closures in the monitor.
  const handleDragStartRef = useRef<(source: any) => void>(() => {});
  const handleDragEndRef = useRef<(source: any, location: any) => void>(
    () => {}
  );

  handleDragStartRef.current = handleDragStart;
  handleDragEndRef.current = handleDragEnd;

  // Register the monitor once; refs call the latest handlers.
  useEffect(() => {
    return monitorForElements({
      onDragStart: ({ source }) => handleDragStartRef.current(source),
      onDrop: ({ source, location }) =>
        handleDragEndRef.current(source, location)
    });
  }, []);

  // RAF auto-scroll on all four edges while a drag is in flight.
  useTimelineDragAutoScroll(scrollContainerRef);

  function handleDragStart(source: any) {
    setDragActionActive(true);
    dragContainerRef.current?.setAttribute('data-drag-active', '');
    if (!planningEntities) return;

    const activePlanningEntityId = source.data?.planningEntityId;
    const activeId = source.data?.id;

    if (!activePlanningEntityId || !activeId) {
      setDragActionActive(false);
      return;
    }

    const isToPlan = source.data.isToPlan;

    const event = resolveDraggedEvent(
      planningEntities,
      activePlanningEntityId,
      activeId,
      isToPlan
    );

    if (!event) return;
    setActiveEvent(event);
    setActiveEntityContainingSelection(activePlanningEntityId);

    setSelectedIds((selected) => (selected.includes(activeId) ? selected : []));
  }

  function handleDragEnd(source: any, location: any) {
    setDragActionActive(false);
    dragContainerRef.current?.removeAttribute('data-drag-active');

    if (!source?.data || Object.keys(source.data).length === 0) {
      return showSnackbar({
        severity: 'warning',
        message: t('dragActionFailed')
      });
    }

    const dropTargets: any[] = location.current.dropTargets;
    if (dropTargets.length === 0) {
      setPendingDropPosition(null);
      return;
    }

    const overData = dropTargets[0].data;
    const overElement = dropTargets[0].element as HTMLElement | undefined;
    const activeData = source.data;

    const overId = overData?.id;
    const overPlanningEntityId = overData?.planningEntityId;
    const activePlanningEntityId = activeData.planningEntityId;
    const activeIsToPlan = activeData.isToPlan;
    const overIsToPlan = !!overData?.isToPlan;
    const activeId = activeData.id;

    if (
      activeIsToPlan === overIsToPlan &&
      overData?.id === 'TO_PLAN_DROPZONE'
    ) {
      return setPendingDropPosition(null);
    }

    const input = location.current.input;
    setPendingDropPosition({ x: input.clientX, y: input.clientY });

    if (activeId === overId) return;

    if (!activeEvent) return onReset();

    if (
      activePlanningEntityId === undefined ||
      (activeEvent === null && selectedIds.length === 0) ||
      (activeIsToPlan && overIsToPlan)
    ) {
      onReset();
      return;
    }

    // Chip preview is centered on cursor; left edge = cursorX − chipWidth/2,
    // translated into route-local coords. Chip's left edge = drop time.
    const chipWidthPx = getItemWidthInPixels(
      timeBarItemWidth,
      activeEvent.durationInSeconds || 0,
      zoomLevel
    );
    const chipLeftInRoute = getChipLeftInRoute(input, overElement, chipWidthPx);

    if (overPlanningEntityId === activePlanningEntityId && !activeIsToPlan) {
      const originalTime = format(new Date(activeEvent.start), 'HH:mm');
      const newDate = getDropDate(
        chipLeftInRoute,
        interval,
        timeBarItemWidth,
        zoomLevel
      );
      if (isValid(newDate)) {
        const newTime = format(newDate, 'HH:mm');
        if (originalTime === newTime) {
          return;
        }
      }
    }

    const ids = selectedIds.length ? selectedIds : [activeId as string];
    const count = selectedIds.length;

    const selectedEvents: Event[] = ids.map(
      (id) => findItem(planningEntities, activePlanningEntityId, id) as Event
    );

    const MULTI_SELECT_ID = 'multiple';

    const start = computeDropStart({
      chipLeftInRoute,
      interval,
      timeBarItemWidth,
      zoomLevel,
      overIsToPlan
    });

    const occurrenceDate = start
      ? format(new Date(start), 'y-MM-dd')
      : startDate;

    const dropPayload = buildDropPayload({
      activeEvent,
      selectedEvents,
      overData,
      start
    });

    setPayload(dropPayload);

    // Freeze the last drag frame: hold a copy of the card at the cursor + dimmed view until the call + refetch settle.
    const frozenChipWidth = (activeData.chipWidth as number) ?? chipWidthPx;
    frozenActiveRef.current = true;
    sawFreezeLoadingRef.current = false;
    // Snap the frozen chip to the row's chip centre so it lands exactly where
    // the real chip sits. Chips are positioned at top: calc(50% - 10px) with
    // height 40px (standard) or top: 50% with height 20px (compact) — both
    // resolve to rowCentre + 10px for the chip's visual centre.
    const overRect = overElement?.getBoundingClientRect();
    const snappedY = overRect
      ? overRect.top + overRect.height / 2 + 10
      : input.clientY;
    setFrozenDrop({
      eventIds: ids,
      position: { x: input.clientX, y: snappedY },
      count: ids.length,
      preview: buildChipPreviewData(
        activeEvent,
        frozenChipWidth,
        detailZoomLevel
      )
    });
    dragContainerRef.current?.setAttribute('data-drag-active', '');

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const id = count > 1 ? MULTI_SELECT_ID : activeEvent.id;

    const scrollToPlanIntoView = () =>
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView();
      }, 150);

    if (new Date(occurrenceDate) < today) {
      if (overIsToPlan) {
        unassignAppointment('ONLY_THIS');
        scrollToPlanIntoView();
        return;
      } else {
        return handlePlanAppointments(dropPayload);
      }
    }

    if (activeEvent?.isRecurring && new Date(occurrenceDate) >= today) {
      setShouldShowPopoverDialog({
        id: activeEvent.id,
        scheduleStart: overData.scheduleStart,
        isUnassignment: overIsToPlan,
        isDragAction: true
      });
    } else if (
      !selectedEvents.some((item) => item.isRecurring) &&
      overIsToPlan
    ) {
      unassignAppointment('ONLY_THIS');
      scrollToPlanIntoView();
    } else {
      handlePlanAppointments(dropPayload);
    }
  }

  return (
    <PopOverConfirmationContext.Provider
      value={{
        onHandleAppointmentConfirmation,
        onClose: onReset,
        isLoading
      }}
    >
      <Box ref={dragContainerRef} display="flex" overflow="hidden">
        <Box sx={getScrollWrapperSx(taskListIsOpen)}>
          <Box display="flex" flexDirection="column" pl={2}>
            <Box
              ref={scrollContainerRef}
              className={TIMELINE_DRAGGABLE_SCROLL_CONTAINER_CLASS}
              flexGrow="1"
              display="flex"
              flexDirection="row"
              onMouseDown={(e: ReactMouseEvent) => {
                // A press that starts on a card must grab the card, not pan the
                // timeline — otherwise the native drag threshold lets the pan
                // steal the first few pixels of movement (perceived grab lag).
                if (
                  (e.target as HTMLElement).closest('[data-appointment-chip]')
                )
                  return;
                setIsDraggingTimeline(true);
              }}
              onMouseUp={() => setIsDraggingTimeline(false)}
              onMouseLeave={() => setIsDraggingTimeline(false)}
              onMouseMove={(e: ReactMouseEvent) => {
                if (!isDraggingTimeline) return;
                dragTimeline(e);
              }}
              sx={getScrollContainerSx(isTablet)}
            >
              <Box width="100%">
                <Stack pb={4} position="relative" zIndex={0}>
                  {isValid(interval?.start) && isValid(interval?.end) && (
                    <TimelineBar
                      innerRef={containerRef}
                      interval={interval}
                      zoomLevel={zoomLevel}
                      scrollContainerRef={scrollContainerRef}
                      timeBarItemWidth={timeBarItemWidth}
                      toPlanDrawerIsOpen={showToPlanDrawer}
                      isTaskListOpen={taskListIsOpen}
                      onChangeZoomLevel={(level: ZoomLevel) => {
                        setZoomLevel(level);
                        dispatch(
                          setUserPreference({
                            key:
                              dateNavigationPeriod === 'week' ||
                              dateNavigationPeriod === 'workweek'
                                ? 'defaultWeekTimelineZoomLevel'
                                : 'defaultDayTimelineZoomLevel',
                            value: level
                          })
                        );
                      }}
                      onChangeCurrentDay={(day: Date) =>
                        setCurrentVisibleDay(day)
                      }
                      onNext={() => scrollTimeline('forward')}
                      onPrevious={() => scrollTimeline('backward')}
                    />
                  )}
                  <Box mt={2}>
                    {Boolean(
                      interval?.durationInSeconds && isValid(interval?.start)
                    ) &&
                      sortedFilteredEntities.map((props) => {
                        return (
                          <Box
                            key={`${props.id}__${props.type}`}
                            display="flex"
                            minWidth={`${
                              timelineZoomWidth + TIMELINE_SUMMARY_WIDTH
                            }px`}
                            width="100%"
                            marginTop="10px"
                          >
                            <Box sx={stickySummarySx}>
                              <Summary name={props.name} />
                            </Box>

                            <TimelineRoutePragmatic
                              interval={interval}
                              timelineRouteWidth={timelineZoomWidth}
                              timeBarItemWidth={timeBarItemWidth}
                              planningEntity={props}
                              activeEntityContainingSelection={
                                activeEntityContainingSelection
                              }
                              zoomLevel={zoomLevel}
                              onChangeActiveEntity={
                                setActiveEntityContainingSelection
                              }
                              onChangeSelection={setSelectedIds}
                              connectAppointmentGrouping={
                                sendConnectAppointmentGrouping
                              }
                              connectSchedule={sendConnectSchedule}
                            />
                          </Box>
                        );
                      })}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>

        <TaskList
          onOpen={setTaskListIsOpen.bind(undefined, true)}
          onClose={setTaskListIsOpen.bind(undefined, false)}
          planningEntities={planningEntities}
          dropzonePadding="10px"
          activeEntityContainingSelection={activeEntityContainingSelection}
          onChangeActiveEntity={setActiveEntityContainingSelection}
          onChangeSelection={setSelectedIds}
          onRouteClick={scrollToDay}
          isTimeline
          DropzoneComponent={ToPlanDropzonePragmatic}
          SortedToPlanListComponent={SortedToPlanChipWrapper({
            zoomLevel,
            timeBarItemWidth
          })}
        />
      </Box>
      {activeEvent && pendingDropPosition && (
        <DragPortalPragmatic
          activeEvent={activeEvent}
          selectedIds={selectedIds}
          pendingDropPosition={pendingDropPosition}
        />
      )}
      <FrozenDropChipPragmatic />
      <LoadingOverlay
        loading={isLoading || isLoadingUnassignAppointment}
        zIndex={60}
      />
    </PopOverConfirmationContext.Provider>
  );
}
